import { createTheme, PaletteOptions } from '@mui/material';
import { ClenaColorType, ClenaStationColorType } from 'types/system/clenaColorType';

declare module '@mui/material/styles/createTheme' {
  interface Theme {
    leftStation: ClenaStationColorType;
    rightStation: ClenaStationColorType;
    grandStation: ClenaStationColorType;
    base: ClenaColorType;
  }
  interface ThemeOptions {
    leftStation: ClenaStationColorType;
    rightStation: ClenaStationColorType;
    grandStation: ClenaStationColorType;
    base: ClenaColorType;
  }
}

const Muiv4ThemePallete: PaletteOptions = {
  common: {
    black: '#000',
    white: '#fff',
  },
  primary: {
    light: '#7986cb',
    main: '#3f51b5',
    dark: '#303f9f',
    contrastText: '#fff',
  },
  secondary: {
    light: '#ff4081',
    main: '#f50057',
    dark: '#c51162',
    contrastText: '#fff',
  },
  error: {
    light: '#e57373',
    main: '#f44336',
    dark: '#d32f2f',
    contrastText: '#fff',
  },
  warning: {
    light: '#ffb74d',
    main: '#ff9800',
    dark: '#f57c00',
    contrastText: 'rgba(0, 0, 0, 0.87)',
  },
  info: {
    light: '#64b5f6',
    main: '#2196f3',
    dark: '#1976d2',
    contrastText: '#fff',
  },
  success: {
    light: '#81c784',
    main: '#4caf50',
    dark: '#388e3c',
    contrastText: 'rgba(0, 0, 0, 0.87)',
  },
  grey: {
    '50': '#fafafa',
    '100': '#f5f5f5',
    '200': '#eeeeee',
    '300': '#e0e0e0',
    '400': '#bdbdbd',
    '500': '#9e9e9e',
    '600': '#757575',
    '700': '#616161',
    '800': '#424242',
    '900': '#212121',
    A100: '#d5d5d5',
    A200: '#aaaaaa',
    A400: '#303030',
    A700: '#616161',
  },
  contrastThreshold: 3,
  tonalOffset: 0.2,
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.54)',
    disabled: 'rgba(0, 0, 0, 0.38)',
  },
  divider: 'rgba(0, 0, 0, 0.12)',
  background: {
    paper: '#fff',
    default: '#fafafa',
  },
  action: {
    active: 'rgba(0, 0, 0, 0.54)',
    hover: 'rgba(0, 0, 0, 0.04)',
    hoverOpacity: 0.04,
    selected: 'rgba(0, 0, 0, 0.08)',
    selectedOpacity: 0.08,
    disabled: 'rgba(0, 0, 0, 0.26)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
    disabledOpacity: 0.38,
    focus: 'rgba(0, 0, 0, 0.12)',
    focusOpacity: 0.12,
    activatedOpacity: 0.12,
  },
  mode: 'light',
};

const theme = createTheme({
  leftStation: {
    contents: '#EEFEFF',
    contentsBorder: '#01C3D5',
    itemBorder: '#01C3D5',
    itemMain: '#01C3D5',
    itemSub: 'rgba(1, 195, 213, 0.5)',
  },
  rightStation: {
    contents: '#FFF9FC',
    contentsBorder: '#FF4386',
    itemBorder: '#FF4386',
    itemMain: '#FF4386',
    itemSub: 'rgba(255, 67, 134, 0.5)',
  },
  grandStation: {
    contents: '#F5FFF7',
    contentsBorder: '#32D7A5',
    itemBorder: '#32D7A5',
    itemMain: '#32D7A5',
    itemSub: 'rgba(50, 215, 165, 0.5)',
  },
  base: {
    tableBorder: '#707070',
    tableRowPrimary: '#FFFFFF',
    tableRowSecondory: '#EBEBEB',
    tableItemBorder: '#707070',
    gray: '#EBEBEB',
    switchDefault: '#BFBFBF',
    switchThumbDefault: '#F1F1F1',
    switchThumbBorderDefault: '#BFBFBF',
    area0: '#FFFFFF',
    area1: '#B5EED8',
    area2: '#EBCFEB',
    area3: '#FFFDB5',
    area4: '#FFC8C8',
    area5: '#B4DFFF',
  },
  typography: {
    fontFamily: [
      '"Helvetica Neue"',
      'Arial',
      '"Hiragino Kaku Gothic ProN"',
      '"Hiragino Sans"',
      'Meiryo',
      'Roboto',
      'sans-serif',
    ].join(','),
  },

  palette: Muiv4ThemePallete,

  components: {
    MuiDialogContent: {
      styleOverrides: {
        root: {
          '&:first-child': {
            paddingTop: 20,
          },
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          marginBottom: 12,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '0.875rem',
          lineHeight: 1.43,
          backgroundColor: '#000000',
          '& #root': {
            height: '100%',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          fontSize: '1.5rem',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          lineHeight: 1,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          overflow: 'initial',
          textOverflow: 'initial',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          lineHeight: '1.1876em',
        },
        input: {
          height: '1.1876em',
          padding: '6px 0 7px',
          '&.Mui-disabled': {
            WebkitTextFillColor: 'unset',
          },
        },
        multiline: {
          padding: '6px 0 7px',
        },
      },
    },
    MuiNativeSelect: {
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: '#fafafa',
        },
      },
    },
  },
});

export default theme;
